// components/ContractModal.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import contractInterceptor from '../../../routes/middleware/ContractInterceptor';
import { logout } from "../../../api/requests/auth-requests";
import { toast } from 'react-toastify';
import mammoth from 'mammoth';

const ContractModal = ({ visible, onClose, contractData, onContractSigned }) => {
    const [loading, setLoading] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [error, setError] = useState(null);
    const [currentContract, setCurrentContract] = useState(null);
    const [contractTextForHash, setContractTextForHash] = useState(null);
    const [showConfirmExit, setShowConfirmExit] = useState(false);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const contractScrollRef = useRef(null);
    const [isScrollWarningVisible, setIsScrollWarningVisible] = useState(false);

    useEffect(() => {
        if (visible && !currentContract) {
            fetchCurrentContract();
        }
    }, [visible]);

    useEffect(() => {
        if (currentContract) {
            setHasScrolledToBottom(false);
        }
    }, [currentContract]);

    const fetchCurrentContract = async () => {
        try {
            setLoading(true);
            setError(null);

            // DOCX dosyasını blob olarak indir
            const blob = await contractInterceptor.downloadContractDocx();

            // Blob'u ArrayBuffer'a çevir
            const arrayBuffer = await blob.arrayBuffer();

            // Mammoth ile HTML'e çevir
            const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
            const html = result.value;

            setCurrentContract(html);

            if (contractData && contractData.contractContent) {
                setContractTextForHash(contractData.contractContent);

            } else {
                throw new Error("Sözleşme metni bilgisi (hash için) sunucudan gelmedi.");
            }
            toast.success('Sözleşme başarıyla yüklendi');
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const handleScroll = (e) => {
        const element = e.target;
        const isAtBottom = Math.abs(
            element.scrollHeight - element.scrollTop - element.clientHeight
        ) < 10;

        if (isAtBottom && !hasScrolledToBottom) {
            setHasScrolledToBottom(true);
            toast.success('Sözleşmeyi sonuna kadar okudunuz');
        }
    };

    const handleSignContract = async () => {
        if (!hasScrolledToBottom) {
            const errorMessage = 'Lütfen önce sözleşmeyi sonuna kadar okuyun';
            setError(errorMessage);
            toast.warning(errorMessage);

            setIsScrollWarningVisible(true);
            setTimeout(() => setIsScrollWarningVisible(false), 2000);
            return;
        }

        if (!accepted) {
            const errorMessage = 'Sözleşmeyi kabul etmeniz gerekmektedir';
            setError(errorMessage);
            toast.warning(errorMessage);
            return;
        }

        if (!contractTextForHash) {
            const errorMessage = 'Sözleşme metni yüklenmemiş';
            setError(errorMessage);
            toast.error(errorMessage);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const serverHash = contractData.contractHash;

            const signRequest = {
                contractContent: contractTextForHash,
                contractHash: serverHash,
                accepted: true
            };

            // signContract artık tam axios response döndürüyor
            const axiosResponse = await contractInterceptor.signContract(signRequest);
            const responseBody = axiosResponse.data; // { statusCode, data, ... }

            if (responseBody.statusCode === 200) {
                // Backend yeni cookie set etti; tokenExp'i güncellemek için body'deki tokeni decode et
                const rawToken = responseBody.data?.token || responseBody.data?.accessToken;
                if (rawToken) {
                    try {
                        const decoded = jwtDecode(rawToken);
                        if (decoded.exp) localStorage.setItem('tokenExp', decoded.exp);
                    } catch {}
                }

                // authUser yoksa (412 login'den gelip hiç set edilmemişse) minimal authUser kur
                if (!localStorage.getItem('authUser')) {
                    localStorage.setItem('authUser', JSON.stringify({
                        user: responseBody.data,
                        loginTime: new Date().toISOString(),
                    }));
                }

                toast.success('Sözleşme başarıyla imzalandı! Artık sistemi kullanabilirsiniz.');
                onContractSigned && onContractSigned();
                // onClose() çağrılmıyor — App.js'de onClose=handleContractModalClose=handleLogout()'a bağlı,
                // başarılı imzalamada logout tetiklememesi için onContractSigned yeterli.
            } else {
                const errorMessage = response.message || 'Sözleşme imzalanırken hata oluştu';
                setError(errorMessage);
                toast.error(errorMessage);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Sözleşme imzalanırken hata oluştu';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadContract = async () => {
        try {
            const { blob, fileName } = await contractInterceptor.downloadContractDocxWithName();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
            toast.success('Sözleşme indiriliyor...');
        } catch (error) {
            toast.error('İndirme başarısız');
        }
    };

    const handleCancel = () => {
        setShowConfirmExit(true);
    };

    const handleConfirmExit = async () => {
        toast.info('Oturum sonlandırılıyor...');
        try {
            await logout();
        } catch {}
        localStorage.removeItem('authUser');
        localStorage.removeItem('tokenExp');
        window.location.href = '/login';
    };

    const handleCancelExit = () => {
        setShowConfirmExit(false);
    };

    if (!visible) return null;

    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1060,
    };

    const modalContentStyle = {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '0',
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    };

    const confirmModalStyle = {
        backgroundColor: '#fff',
        borderRadius: '8px',
        padding: '0',
        width: '450px',
        maxWidth: '90vw',
        boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
    };

    const modalHeaderStyle = {
        padding: '20px 24px',
        borderBottom: '1px solid #e8e8e8',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    };

    const modalBodyStyle = {
        padding: '24px',
        overflowY: 'auto',
        flex: 1,
    };

    const modalFooterStyle = {
        padding: '16px 24px',
        borderTop: '1px solid #e8e8e8',
        backgroundColor: '#f8f9fa',
        borderRadius: '0 0 8px 8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
    };

    const buttonStyle = {
        padding: '8px 16px',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: '500',
        fontSize: '14px',
        minWidth: '80px',
        transition: 'all 0.3s',
    };

    const primaryButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#1890ff',
        color: 'white',
    };

    const dangerButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#ff4d4f',
        color: 'white',
    };

    const secondaryButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#fff',
        color: '#666',
        border: '1px solid #d9d9d9',
    };

    const downloadButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#52c41a',
        color: 'white',
    };

    const contractViewerStyle = {
        border: '2px solid #d9d9d9',
        borderRadius: '6px',
        height: '500px',
        marginBottom: '16px',
        backgroundColor: '#fff',
        position: 'relative',
        overflowY: 'auto',
    };

    const scrollIndicatorStyle = {
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        backgroundColor: hasScrolledToBottom ? '#52c41a' : '#ff4d4f',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 10,
        pointerEvents: 'none',
    };

    const alertStyle = {
        padding: '12px 16px',
        borderRadius: '6px',
        marginBottom: '16px',
        border: '1px solid',
    };

    const infoAlertStyle = {
        ...alertStyle,
        backgroundColor: '#e6f7ff',
        borderColor: '#91d5ff',
        color: '#0050b3',
    };

    const errorAlertStyle = {
        ...alertStyle,
        backgroundColor: '#fff2f0',
        borderColor: '#ffccc7',
        color: '#a8071a',
    };

    const warningAlertStyle = {
        ...alertStyle,
        backgroundColor: '#fffbe6',
        borderColor: '#ffe58f',
        color: '#ad6800',
    };

    const ConfirmationModal = () => (
        <div style={modalOverlayStyle}>
            <div style={confirmModalStyle}>
                <div style={modalHeaderStyle}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#ff4d4f' }}>
                        Dikkat
                    </h3>
                </div>
                <div style={{ padding: '24px' }}>
                    <p style={{
                        margin: 0,
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: '#333'
                    }}>
                        <strong>Sözleşmeyi imzalamadan sistemi kullanamazsınız.</strong>
                        <br /><br />
                        Çıkmak istediğinizden emin misiniz?
                    </p>
                </div>
                <div style={modalFooterStyle}>
                    <button
                        style={secondaryButtonStyle}
                        onClick={handleCancelExit}
                    >
                        İptal
                    </button>
                    <button
                        style={dangerButtonStyle}
                        onClick={handleConfirmExit}
                    >
                        Evet, Çıkış Yap
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <div style={modalOverlayStyle}>
                <div style={modalContentStyle}>
                    <div style={modalHeaderStyle}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                            Hizmet Sözleşmesi
                        </h3>
                    </div>

                    <div style={modalBodyStyle}>
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <div className="spinner-border text-primary" role="status">
                                    <span className="sr-only">Yükleniyor...</span>
                                </div>
                                <p style={{ marginTop: '16px', color: '#666' }}>
                                    Sözleşme hazırlanıyor...
                                </p>
                            </div>
                        ) : (
                            <>
                                <div style={infoAlertStyle}>
                                    <strong>Önemli Bilgilendirme</strong>
                                    <br />
                                    Sistemi kullanmaya devam edebilmek için aşağıdaki hizmet sözleşmesini
                                    <strong> sonuna kadar okumanız</strong> ve kabul etmeniz gerekmektedir.
                                </div>

                                {error && (
                                    <div style={errorAlertStyle}>
                                        {error}
                                        <button
                                            style={{
                                                float: 'right',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '20px',
                                                color: '#a8071a',
                                                lineHeight: 1,
                                            }}
                                            onClick={() => setError(null)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}

                                {contractData && (
                                    <div style={{
                                        marginBottom: '16px',
                                        padding: '12px 16px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                    }}>
                                        <strong>Sözleşme Bilgileri:</strong>
                                        <br />
                                        Güncel Versiyon: <strong>{contractData.currentContractVersion}</strong>
                                        {contractData.signedContractVersion && (
                                            <>
                                                <br />
                                                İmzalanmış Versiyon: <strong>{contractData.signedContractVersion}</strong>
                                            </>
                                        )}
                                    </div>
                                )}

                                {!hasScrolledToBottom && (
                                    <div
                                        style={{
                                            ...warningAlertStyle,
                                            marginBottom: '16px',
                                            // Dinamik stil ve animasyon
                                            transition: 'all 0.2s ease-in-out',
                                            boxShadow: isScrollWarningVisible ? '0 0 10px #ffc53d' : 'none', // Titreşim efekti için shadow
                                            transform: isScrollWarningVisible ? 'scale(1.02)' : 'scale(1)', // Hafif büyütme efekti
                                            // Bu uyarı görünür olmalı, isScrollWarningVisible sadece ek bir animasyon tetikler.
                                            opacity: 1,
                                        }}
                                    >
                                        <strong>Dikkat</strong>
                                        <br />
                                        Sözleşmeyi imzalayabilmek için lütfen sözleşmenin tamamını okuyun ve en alta kadar kaydırın.
                                    </div>
                                )}

                                <div
                                    style={contractViewerStyle}
                                    ref={contractScrollRef}
                                    onScroll={handleScroll}
                                >
                                    {currentContract ? (
                                        <>
                                            <div
                                                style={{
                                                    padding: '20px',
                                                    fontSize: '14px',
                                                    lineHeight: '1.6',
                                                }}
                                                dangerouslySetInnerHTML={{ __html: currentContract }}
                                            />
                                        </>
                                    ) : (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            height: '100%',
                                            color: '#999'
                                        }}>
                                            Sözleşme yükleniyor...
                                        </div>
                                    )}
                                </div>

                                <div style={{
                                    marginBottom: '16px',
                                    opacity: hasScrolledToBottom ? 1 : 0.5,
                                    transition: 'opacity 0.3s',
                                }}>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
                                        padding: '12px',
                                        backgroundColor: hasScrolledToBottom ? '#f6ffed' : '#f5f5f5',
                                        borderRadius: '6px',
                                        border: `2px solid ${hasScrolledToBottom ? '#52c41a' : '#d9d9d9'}`,
                                        transition: 'all 0.3s',
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={accepted}
                                            onChange={(e) => setAccepted(e.target.checked)}
                                            disabled={!hasScrolledToBottom}
                                            style={{
                                                marginRight: '12px',
                                                marginTop: '2px',
                                                cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
                                            }}
                                        />
                                        <span style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                            Yukarıdaki hizmet sözleşmesini okudum, anladım ve
                                            <strong> firma adına kabul ediyorum</strong>.
                                        </span>
                                    </label>
                                </div>

                                <div style={infoAlertStyle}>
                                    <strong>Güvenlik Bilgisi</strong>
                                    <br />
                                    Bu sözleşme firma adına imzalanacaktır. İmzalama işlemi sonrasında
                                    IP adresiniz, tarih/saat bilgisi ve tarayıcı bilgileriniz güvenlik
                                    amacıyla kaydedilecektir.
                                </div>
                            </>
                        )}
                    </div>

                    <div style={modalFooterStyle}>
                        <button
                            style={secondaryButtonStyle}
                            onClick={handleCancel}
                        >
                            Çıkış Yap
                        </button>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                style={{
                                    ...primaryButtonStyle,
                                    opacity: (!accepted || !hasScrolledToBottom || !contractTextForHash || loading) ? 0.6 : 1,
                                    cursor: (!accepted || !hasScrolledToBottom || !contractTextForHash || loading) ? 'not-allowed' : 'pointer'
                                }}
                                onClick={handleSignContract}
                                disabled={!accepted || !hasScrolledToBottom || !contractTextForHash || loading}
                            >
                                {loading ? 'İmzalanıyor...' : 'Sözleşmeyi İmzala'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showConfirmExit && <ConfirmationModal />}
        </>
    );
};

export default ContractModal;